import Close from '@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';
import { AtlasMuiThemeProvider } from 'src/theme/atlas/atlasMuiThemeProvider';
import { toastEventBus, ToastEvent } from './toastEventBus';

interface Props {
  children: React.ReactNode;
}

type ActiveToast = ToastEvent & { id: number };

export const AtlasToastProvider = ({ children }: Props) => {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const nextId = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  useEffect(() => {
    const timersMap = timers.current;
    const handler = (event: ToastEvent) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { ...event, id }]);
      const duration =
        event.autoClose === false ? null : (event.autoClose ?? 5000);
      if (duration !== null) {
        timersMap.set(
          id,
          setTimeout(() => removeToast(id), duration),
        );
      }
    };
    const clearHandler = () => {
      timersMap.forEach((timer) => clearTimeout(timer));
      timersMap.clear();
      setToasts([]);
    };
    toastEventBus.subscribe(handler);
    toastEventBus.subscribeClear(clearHandler);
    return () => {
      toastEventBus.unsubscribe(handler);
      toastEventBus.unsubscribeClear(clearHandler);
      timersMap.forEach((timer) => clearTimeout(timer));
      timersMap.clear();
    };
  }, [removeToast]);

  return (
    <>
      {children}
      <AtlasMuiThemeProvider>
        {toasts.map((toast, index) => (
          <Snackbar
            key={toast.id}
            open
            transitionDuration={0}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            onClose={() => undefined}
            style={{ top: `${8 + index * 68}px` }}
            sx={{ pointerEvents: 'none' }}
          >
            <Alert
              severity={toast.severity}
              aria-live="polite"
              data-testid={`atlas-toast-alert--${toast.severity}`}
              sx={{ maxWidth: 480, pointerEvents: 'auto' }}
              action={
                <Button
                  variant="text"
                  size="medium"
                  aria-label="Dismiss"
                  onClick={() => removeToast(toast.id)}
                >
                  <Close />
                </Button>
              }
            >
              {toast.description ? (
                <>
                  <strong>{toast.message}</strong>
                  <br />
                  {toast.description}
                </>
              ) : (
                toast.message
              )}
            </Alert>
          </Snackbar>
        ))}
      </AtlasMuiThemeProvider>
    </>
  );
};
